import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, ShieldAlert, Lock } from 'lucide-react';

interface SuspiciousLoginAlertProps {
  ipAddress: string;
  onClose: () => void;
}

export function SuspiciousLoginAlert({ ipAddress, onClose }: SuspiciousLoginAlertProps) {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <div className="bg-yellow-50 rounded-t-lg px-6 py-4 border-b border-yellow-100 flex items-center">
          <div className="bg-yellow-100 p-2 rounded-full">
            <ShieldAlert className="h-6 w-6 text-yellow-600" />
          </div>
          <h3 className="ml-3 text-lg font-medium text-yellow-800">
            Security Alert
          </h3>
          <button 
            onClick={handleClose}
            className="ml-auto text-yellow-500 hover:text-yellow-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3" />
            <div>
              <p className="text-gray-700 mb-4">
                You're logging in from an IP address that differs from your recent login locations:
              </p>
              <div className="bg-gray-50 p-3 rounded-md mb-4 font-mono text-sm">
                {ipAddress}
              </div>
              <p className="text-gray-700 mb-4">
                If this wasn't you, your account may be compromised. We recommend:
              </p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Changing your password immediately</li>
                <li>Reviewing your recent account activity</li>
                <li>Enabling two-factor authentication if available</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Dismiss
            </button>
            <button
              onClick={() => {
                // This would typically navigate to password change page
                handleClose();
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 flex items-center"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}